/**
 * Assembles the relationship graph behind a single customer payment.
 *
 * Kept out of the payment data client because the two have different shapes:
 * the client reads and writes one row, while this fans out across four tables
 * to answer "what does this payment settle, and what else touched it".
 */

import { CUSTOMER_PAYMENT_REMAINDER_REFERENCE } from "@/features/credits/utils/overpayment-credit";
import type {
  CustomerPayment,
  CustomerPaymentEstimateSummary,
  CustomerPaymentInvoiceSummary,
  CustomerPaymentRelated,
  CustomerPaymentRemainderCredit,
  CustomerPaymentSibling,
} from "@/features/customer-payments/types";
import { getBackendClient } from "@/lib/backend";
import { cast } from "@/lib/utils/query-helpers";

const backend = getBackendClient();

const RELATED_SIBLING_LIMIT = 20;
const RELATED_CREDIT_LIMIT = 10;

async function getInvoiceSummary(
  invoiceId: string | null,
): Promise<CustomerPaymentInvoiceSummary | null> {
  if (!invoiceId) {
    return null;
  }

  const { data, error } = await backend.data
    .from("gl_invoices")
    .select("id, uid, total_amount, total_paid, balance, payment_status")
    .eq("id", invoiceId)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  return data ? cast<CustomerPaymentInvoiceSummary>(data) : null;
}

/**
 * The estimate edge is reverse and one hop out: `gl_invoices` has no estimate
 * column, so the originating estimate is the one whose `converted_invoice_id`
 * points at this payment's invoice.
 */
async function getConvertingEstimate(
  invoiceId: string | null,
): Promise<CustomerPaymentEstimateSummary | null> {
  if (!invoiceId) {
    return null;
  }

  const { data, error } = await backend.data
    .from("gl_estimates")
    .select("id, uid, total_amount")
    .eq("converted_invoice_id", invoiceId)
    .limit(1);

  if (error) {
    throw new Error(error.message);
  }

  const rows = cast<CustomerPaymentEstimateSummary[]>(data || []);
  return rows[0] ?? null;
}

async function getSiblingPayments(
  invoiceId: string | null,
  paymentId: string,
): Promise<CustomerPaymentSibling[]> {
  if (!invoiceId) {
    return [];
  }

  const { data, error } = await backend.data
    .from("gl_customer_payments")
    .select("id, uid, amount, date, method")
    .eq("invoice_id", invoiceId)
    .neq("id", paymentId)
    .order("date", { ascending: false })
    .limit(RELATED_SIBLING_LIMIT);

  if (error) {
    throw new Error(error.message);
  }

  return cast<CustomerPaymentSibling[]>(data || []);
}

/**
 * `gl_customer_credits` carries no originating-payment column, so a remainder
 * credit is identified by the account plus the sentinel reference
 * `xan_apply_customer_payment` stamps. These are account-level credits, not
 * this payment's, and the surface says so.
 */
async function getAccountRemainderCredits(
  accountId: string | null,
): Promise<CustomerPaymentRemainderCredit[]> {
  if (!accountId) {
    return [];
  }

  const { data, error } = await backend.data
    .from("gl_customer_credits")
    .select("id, uid, amount, date")
    .eq("account_id", accountId)
    .eq("reference", CUSTOMER_PAYMENT_REMAINDER_REFERENCE)
    .order("date", { ascending: false })
    .limit(RELATED_CREDIT_LIMIT);

  if (error) {
    throw new Error(error.message);
  }

  return cast<CustomerPaymentRemainderCredit[]>(data || []);
}

/**
 * Resolves everything the payment detail surface links out to. Kept separate
 * from the payment query so a relationship failure still leaves the payment's
 * own fields on screen.
 *
 * Takes the loaded payment rather than an id: every caller already holds the
 * row, and re-reading it here would be a second fetch of the same record under
 * a key TanStack Query cannot dedupe against the first.
 */
export async function getCustomerPaymentRelated(
  payment: CustomerPayment,
): Promise<CustomerPaymentRelated> {
  const [invoice, estimate, siblingPayments, remainderCredits] = await Promise.all([
    getInvoiceSummary(payment.invoice_id),
    getConvertingEstimate(payment.invoice_id),
    getSiblingPayments(payment.invoice_id, payment.id),
    getAccountRemainderCredits(payment.account_id),
  ]);

  return {
    account: payment.gl_accounts ?? null,
    estimate,
    invoice,
    remainderCredits,
    siblingPayments,
  };
}
